/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'

/**
 * Shared styles for Scheduler Wizard components
 * Reduces code duplication across section components
 */

// ============================================
// Button Styles
// ============================================

export const primaryButtonStyles = css`
  padding: 8px 16px;
  background-color: ${colors.PRIMARY};
  border: none;
  color: ${colors.PRIMARY_TEXT};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${colors.PRIMARY_HOVER};
  }
`

export const outlinePrimaryButtonStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: transparent;
  border: 1px solid ${colors.PRIMARY};
  border-radius: 8px;
  color: ${colors.PRIMARY};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background-color: ${colors.PRIMARY};
    color: ${colors.PRIMARY_TEXT};
  }
`

export const outlineNeutralButtonStyles = css`
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid ${colors.NEUTRAL_200};
  border-radius: 6px;
  color: ${colors.PRIMARY};
  cursor: pointer;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 500;

  &:hover {
    border-color: ${colors.PRIMARY};
    background-color: ${colors.PRIMARY}0D;
  }
`

/**
 * Outlined accent button — used for the AI Suggest button next to "+ Add
 * Constraint". Same shape as outlinePrimaryButtonStyles but with a subtle
 * accent treatment (faint background tint) to differentiate it from the
 * primary action without competing with it visually.
 */
export const outlineAccentButtonStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: ${colors.PRIMARY}1A;
  border: 1px solid ${colors.PRIMARY};
  border-radius: 8px;
  color: ${colors.PRIMARY};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background-color: ${colors.PRIMARY};
    color: ${colors.PRIMARY_TEXT};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const textButtonStyles = css`
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: ${colors.PRIMARY};
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  padding: 4px;

  &:hover {
    text-decoration: underline;
  }
`

// ============================================
// Icon Button Styles
// ============================================

export const iconButtonStyles = css`
  background: transparent;
  border: none;
  color: ${colors.WHITE};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;

  &:hover {
    color: ${colors.PRIMARY};
  }
`

export const iconButtonDangerStyles = css`
  ${iconButtonStyles}

  &:hover {
    color: ${colors.ERROR_300};
  }
`

// ============================================
// Table Styles
// ============================================

export const darkTableStyles = css`
  .ant-table {
    background: transparent;
  }
  .ant-table-thead > tr > th {
    background-color: ${colors.SECONDARY_300} !important;
    border-bottom: none !important;
    color: ${colors.NEUTRAL_70} !important;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 17.4px;
    text-transform: uppercase;
    padding: 4px 16px !important;
  }
  .ant-table-tbody > tr {
    background-color: transparent;
  }
  .ant-table-tbody > tr:nth-of-type(even) {
    background-color: ${colors.SECONDARY_400};
  }
  .ant-table-tbody > tr > td {
    color: ${colors.WHITE};
    border-bottom: none !important;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20.3px;
    padding: 8px 16px !important;
  }
  .ant-table-tbody > tr:hover > td {
    background-color: ${colors.SECONDARY_300} !important;
  }
  .ant-table-tbody > tr.disabled-row > td {
    opacity: 0.5;
  }
  .ant-table-tbody > tr.disabled-row:hover > td {
    background-color: #212935 !important;
  }
`

// ============================================
// Action Buttons Container
// ============================================

export const actionButtonsContainerStyles = css`
  display: flex;
  gap: 8px;
  align-items: center;
`

// ============================================
// Card/Section Styles
// ============================================

export const sectionCardStyles = css`
  background-color: ${colors.SECONDARY_400};
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_300};
  padding: 16px;
`

export const expandButtonStyles = css`
  background: transparent;
  border: none;
  color: ${colors.WHITE};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: transform 0.2s;

  &:hover {
    color: ${colors.PRIMARY};
  }
`

// ============================================
// Badge Styles
// ============================================

export const primaryBadgeStyles = css`
  font-size: 12px;
  font-weight: 700;
  padding: 4px 8px;
  background-color: ${colors.PRIMARY};
  color: ${colors.PRIMARY_TEXT};
  border-radius: 4px;
`

export const infoBadgeStyles = css`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  background-color: ${colors.INFO_300};
  color: ${colors.WHITE};
  border-radius: 4px;
`

export const warningBadgeStyles = css`
  font-size: 12px;
  font-weight: 700;
  padding: 4px 8px;
  background-color: ${colors.WARNING_300};
  color: ${colors.SECONDARY_300};
  border-radius: 4px;
`

export const errorBadgeStyles = css`
  font-size: 12px;
  font-weight: 700;
  padding: 4px 8px;
  background-color: ${colors.ERROR_300};
  color: ${colors.WHITE};
  border-radius: 4px;
  text-transform: uppercase;
`

export const neutralBadgeStyles = css`
  font-size: 12px;
  padding: 4px 8px;
  background-color: ${colors.SECONDARY_200};
  color: ${colors.WHITE};
  border-radius: 4px;
`

// ============================================
// Section Divider Styles
// ============================================

export const sectionDividerStyles = css`
  width: 100%;
  height: 1px;
  background-color: #3a4659;
  border: none;
  margin: 0;
`

// ============================================
// Dark Portal Styles (Global)
// ============================================

/**
 * Global styles for dark-themed components rendered in portals.
 * Includes Select dropdowns and message notifications.
 * Used with <Global styles={darkPortalStyles} />.
 */
export const darkPortalStyles = css`
  /* Select dropdown styles */
  .dark-select-popup.ant-select-dropdown,
  .dark-select-popup .ant-select-dropdown {
    background-color: ${colors.SECONDARY_400} !important;
    border: 1px solid ${colors.SECONDARY_200} !important;
  }

  .dark-select-popup .ant-select-item,
  .dark-select-popup .ant-select-item-option-content {
    color: ${colors.WHITE} !important;
  }

  .dark-select-popup
    .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
    background-color: ${colors.SECONDARY_300} !important;
  }

  .dark-select-popup
    .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background-color: ${colors.PRIMARY} !important;
  }

  .dark-select-popup
    .ant-select-item-option-selected:not(.ant-select-item-option-disabled)
    .ant-select-item-option-content {
    color: ${colors.PRIMARY_TEXT} !important;
  }

  .dark-select-popup .ant-select-item-group {
    color: ${colors.NEUTRAL_100} !important;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
  }

  /* Message/notification styles */
  .ant-message .ant-message-notice-content {
    background-color: ${colors.SECONDARY_400} !important;
    color: ${colors.WHITE} !important;
  }

  .ant-message .ant-message-error .anticon,
  .ant-message .ant-message-success .anticon,
  .ant-message .ant-message-warning .anticon,
  .ant-message .ant-message-info .anticon {
    color: inherit;
  }

  .ant-message .ant-message-error {
    color: ${colors.ERROR_300} !important;
  }

  .ant-message .ant-message-success {
    color: ${colors.SUCCESS_300} !important;
  }
`

// ============================================
// Select All Dropdown Helper
// ============================================

/**
 * CSS for the "Select All" item rendered at the top of multi-select dropdowns.
 */
export const selectAllItemStyles = css`
  padding: 8px 12px;
  cursor: pointer;
  color: ${colors.PRIMARY};
  border-bottom: 1px solid ${colors.SECONDARY_200};
  &:hover {
    background-color: ${colors.SECONDARY_400};
  }
`
